import { Button, DatePicker, Form, Select } from "antd";
import { ReactElement, useEffect, useState } from "react";

import "./edit-personal-details.scss";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AxiosResponse } from "axios";

import toast from "react-hot-toast";
import dayjs from "dayjs";
import ReactCountryFlag from "react-country-flag";
import { PlusSquareOutlined } from "@ant-design/icons";
import CustomModal from "src/components/custom-modal/custom-modal";
import Label from "src/components/label/label.component";
import CustomInput from "src/components/custom-input/custom-input.component";
import CollapseComponent from "src/components/collapse.component";
import DeleteModal from "src/components/delete-modal/delete-modal";
import { LOADING } from "src/store/actions";
import instance from "src/store/axios";
import { getStandingDataOptions } from "src/utils/helper/functions";
import HijriDatePicker from "react-multi-date-picker";
import arabic from "react-date-object/calendars/arabic";
import arabic_en from "react-date-object/locales/arabic_en";
import DateObject from "react-date-object";
import gregorian from "react-date-object/calendars/gregorian";
import gregorian_en from "react-date-object/locales/gregorian_en";
import ContactPersonForm from "../../../edit-contact-persons/contact-person-form";

const { Option } = Select;

const EditPersonalDetails: React.FC<{
  countriesData: any;
  standingData: any;
  accountDetails: any;
  onSuccess: () => void;
}> = ({
  countriesData,
  standingData,
  accountDetails,
  onSuccess,
}): ReactElement => {
    const { accountId, activeKyc } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const isApproveCase = queryParams.get("approve");
    const [form] = Form.useForm();
    const values = Form.useWatch([], form);
    const [submitForm, setSubmitForm] = useState(false);

    const [openUpdateFatca, setOpenUpdateFatca] = useState<boolean>(false);
    const [openContactPersonsModal, setOpenContactPersonsModal] =
      useState<boolean>(false);
    const [openDeleteModal, setOpenDeleteModal] = useState<any>(false);
    const [selectedContactPerson, setSelectedContactPerson] = useState<any>();
    const [contactPersonsArray, setContactPersonsArray] = useState<any>([]);
    const residentialCountryId = Form.useWatch("residentialCountryId", form);
    const isSaudiResidential = residentialCountryId === 154;
    const nationalityId = Form.useWatch("nationalityId", form);
    const countryId = Form.useWatch("countryId", form);

    useEffect(() => {
      if (nationalityId === 154) {
        form.setFieldsValue({ idTypeId: 192 }); // National ID
      } else if (countryId === 154) {
        form.setFieldsValue({ idTypeId: 193 }); // Iqama
      } else {
        form.setFieldsValue({ idTypeId: -1 }); // Other ID
      }
    }, [nationalityId, countryId]);

    useEffect(() => {
      form.validateFields({ validateOnly: true }).then(
        () => {
          setSubmitForm(true);
        },
        () => {
          setSubmitForm(false);
        }
      );
    }, [values]);

    const onSubmit = async (values: any): Promise<void> => {
      dispatch({ type: LOADING, payload: true });

      values.accountCode = +values.accountCode;

      if (values.birthDateHijri) {
        const hijriBirthDate = new DateObject(values.birthDateHijri);
        hijriBirthDate.locale = arabic_en;
        values.birthDateHijri = hijriBirthDate.format("YYYY-MM-DD");
        values.birthDate = hijriBirthDate
          .convert(gregorian, gregorian_en)
          .toDate()
          .toISOString();
      } else if (values.birthDate) {
        values.birthDate = dayjs(values.birthDate).toISOString();
      }

      if (values.expiryDateHijri) {
        const hijriExpiryDate = new DateObject(values.expiryDateHijri);
        hijriExpiryDate.locale = arabic_en;
        values.expiryDateHijri = hijriExpiryDate.format("YYYY-MM-DD");
        values.civilIdExpiry = hijriExpiryDate
          .convert(gregorian, gregorian_en)
          .format("YYYY-MM-DD");
      } else if (values.civilIdExpiry) {
        values.civilIdExpiry = dayjs(values.civilIdExpiry).format("YYYY-MM-DD");
      }

      values.kycId = accountDetails.accountInfo[0]?.id;
      values.numberDependents = values.numberDependents
        ? +values.numberDependents
        : null;
      values.branch = "SA";

      if (!values.otherNationalityId) {
        values.otherNationalityId = null;
      }

      try {
        await instance.post(
          `/${process.env.REACT_APP_ENDPOINT_VERSION}/account/${accountId}/personal/update`,
          values
        );
        toast.success("Personal data updated successfully");
        onSuccess();
        // if (values.nationalityId === 187 || values.otherNationalityId === 187) {
        //   setOpenUpdateFatca(true);
        // }
      } catch (error: any) {
        if (error?.response?.status !== 401) {
          toast.error(error?.response?.data?.message || "An error occurred");
          localStorage.setItem(
            "star_prev_url",
            `/home/new-kyc/${accountDetails?.id}/${accountDetails?.pendingKycId}`
          );
        }
      }

      dispatch({ type: LOADING, payload: false });
    };

    const getContactPersons = async (): Promise<void> => {
      try {
        const response: AxiosResponse<any> = await instance.get(
          `/${process.env.REACT_APP_ENDPOINT_VERSION}/account/${accountId}?kycId=${activeKyc}`
        );
        if (response.data.data?.contactPersons?.length) {
          setContactPersonsArray(response.data.data?.contactPersons);
        } else {
          setContactPersonsArray([{}]);
        }
      } catch (error: any) {
        if (error?.response?.status !== 401) {
          toast.error(error?.response?.data?.message || "An error occurred");
        }
      }
    };

    const onContactPersonSubmit = async (values: any): Promise<void> => {
      dispatch({ type: LOADING, payload: true });

      values.kycId = accountDetails.accountInfo[0]?.id;
      values.branch = "SA";

      try {
        if (!values?.id) {
          delete values?.id;
          await instance.post(
            `/${process.env.REACT_APP_ENDPOINT_VERSION}/account/${accountId}/contact-persons`,
            values
          );
        } else {
          await instance.post(
            `/${process.env.REACT_APP_ENDPOINT_VERSION}/account/${accountId}/contact-persons/${values?.id}`,
            values
          );
        }

        toast.success("Contact person saved successfully");
        await getContactPersons();
      } catch (error: any) {
        if (error?.response?.status !== 401) {
          toast.error(error?.response?.data?.message || "An error occurred");
        }
      }

      dispatch({ type: LOADING, payload: false });
    };

    const deleteContactPerson = async (): Promise<void> => {
      dispatch({ type: LOADING, payload: true });

      try {
        await instance.post(
          `/${process.env.REACT_APP_ENDPOINT_VERSION}/account/${accountId}/contact-persons/${selectedContactPerson?.id}/delete`
        );
        toast.success("Contact person deleted successfully");
        setOpenDeleteModal(false);
        await getContactPersons();
      } catch (error: any) {
        if (error?.response?.status !== 401) {
          toast.error(error?.response?.data?.message || "An error occurred");
        }
      }

      dispatch({ type: LOADING, payload: false });
    };

    useEffect(() => {
      if (openContactPersonsModal && accountDetails) {
        getContactPersons();
      }
    }, [openContactPersonsModal, accountDetails]);

    return (
      <div className="edit-personal-details-container flex flex-col mt-5">
        {accountDetails && (
          <Form
            className="add-edit-form flex flex-col gap-5"
            onFinish={onSubmit}
            scrollToFirstError
            initialValues={{
              accountCode: accountDetails?.accountCode,
              accountTypeId: accountDetails?.accountTypeId,
              civilIdNumber: accountDetails?.accountInfo[0]?.civilIdNumber,
              civilIdExpiry: accountDetails?.accountInfo[0]?.civilIdExpiry
                ? dayjs(accountDetails?.accountInfo[0]?.civilIdExpiry)
                : null,
              passportNumber: accountDetails?.accountInfo[0]?.passportNumber,
              passportExpiry: accountDetails?.accountInfo[0]?.passportNumber
                ? dayjs(accountDetails?.accountInfo[0]?.passportExpiry)
                : null,
              nationalityId: accountDetails?.accountInfo[0]?.nationalityId,
              otherNationalityId:
                accountDetails?.accountInfo[0]?.otherNationalityId,
              birthDate: accountDetails?.accountInfo[0]?.birthDate
                ? dayjs(accountDetails?.accountInfo[0]?.birthDate)
                : null,
              titleId: accountDetails?.accountInfo[0]?.titleId,
              otherTitle: accountDetails?.accountInfo[0]?.otherTitle,
              firstName: accountDetails?.accountInfo[0]?.firstName,
              secondName: accountDetails?.accountInfo[0]?.secondName,
              thirdName: accountDetails?.accountInfo[0]?.thirdName,
              familyName: accountDetails?.accountInfo[0]?.familyName,
              firstNameAr: accountDetails?.accountInfo[0]?.firstNameAr,
              secondNameAr: accountDetails?.accountInfo[0]?.secondNameAr,
              thirdNameAr: accountDetails?.accountInfo[0]?.thirdNameAr,
              familyNameAr: accountDetails?.accountInfo[0]?.familyNameAr,
              birthPlaceId: accountDetails?.accountInfo[0]?.birthPlaceId,
              countryId: accountDetails?.accountInfo[0]?.countryId,
              birthPlaceCity: accountDetails?.accountInfo[0]?.birthPlaceCity,
              genderId: accountDetails?.accountInfo[0]?.genderId,
              numberDependents: +accountDetails?.accountInfo[0]?.numberDependents,
              maritalStatusId: accountDetails?.accountInfo[0]?.maritalStatusId,
              idTypeId: accountDetails?.accountInfo[0]?.idTypeId,
              isBeneficialOwner: accountDetails?.accountInfo[0]?.isBeneficialOwner,
              benefeicialOwnerId: accountDetails?.accountInfo[0]?.benefeicialOwnerId,
              guardianTitleId: accountDetails?.accountInfo[0].guardianTitle?.id,
              guardianNameEn: accountDetails?.accountInfo[0].guardianNameEn,
              guardianResidentCountryId:
                accountDetails?.accountInfo[0].guardianResidentCountry?.id,
              guardianCivilId: accountDetails?.accountInfo[0].guardianCivilId,
              guardianCivilExpiry: accountDetails?.accountInfo[0].guardianCivilExpiry
                ? dayjs(accountDetails?.accountInfo[0].guardianCivilExpiry)
                : undefined,
              guardianCivilIssuePlace: accountDetails?.accountInfo[0].guardianCivilIssuePlace,
              guardianMobile: accountDetails?.accountInfo[0].guardianMobile,
              guardianRelationshipWithApplicant: accountDetails?.accountInfo[0].guardianRelationshipWithApplicant,
              guardianDeedNumber: accountDetails?.accountInfo[0].guardianDeedNumber,
              guardianDeedDate: accountDetails?.accountInfo[0].guardianDeedDate
                ? dayjs(accountDetails?.accountInfo[0].guardianDeedDate)
                : undefined,
              birthDateHijri: accountDetails?.accountInfo[0]?.birthDate
                ? new DateObject({
                  date: new Date(accountDetails?.accountInfo[0]?.birthDate),
                  calendar: arabic,
                  locale: arabic_en,
                })
                : null,
              expiryDateHijri: accountDetails?.accountInfo[0]?.civilIdExpiry
                ? new DateObject({
                  date: new Date(accountDetails?.accountInfo[0]?.civilIdExpiry),
                  calendar: arabic,
                  locale: arabic_en,
                })
                : null,
            }}
            form={form}
          >
            <div className="flex gap-3 items-center">
              <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
              <h3 className=" text-lg font-semibold">
                {accountDetails?.clientSubType?.description === "Minor"
                  ? "Minor details"
                  : "Personal details"}
              </h3>
            </div>
            <div className="fields flex justify-between flex-wrap">
              <div className="fields flex justify-between flex-wrap">
                <div className="field flex flex-col">
                  <Label text="Account code" required />
                  <Form.Item
                    name="accountCode"
                    rules={[
                      {
                        required: true,
                        message: "Please input account code",
                      },
                      {
                        pattern: /^2\d{5}$/,
                        message: "Account code must be 6 digits and start with 2",
                      },
                    ]}
                  >
                    <CustomInput
                      placeholder="Enter account code"
                      maxLength={6}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        form.setFieldValue("accountCode", value);
                      }}
                    />
                  </Form.Item>
                </div>
                <div className="field flex flex-col">
                  <Label text="Title" required />
                  <Form.Item
                    name="titleId"
                    rules={[
                      {
                        required: true,
                        message: "Please select title",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select title"
                      options={[
                        ...getStandingDataOptions(
                          standingData,
                          "title",
                          ["sa", "gc"],
                          true
                        ),
                      ]}
                    />
                  </Form.Item>
                </div>

                {form.getFieldValue("titleId") == "-1" && (
                  <div className="field flex flex-col">
                    <Label text="Other title" required />
                    <Form.Item
                      name="otherTitle"
                      rules={[
                        {
                          required: true,
                          message: "Please input title",
                        },
                      ]}
                    >
                      <CustomInput placeholder="Enter other title" />
                    </Form.Item>
                  </div>
                )}
              </div>

              <div className="field flex items-start justify-between">
                <div className="flex flex-col gap-2 w-[48%]">
                  <Label text="En. First Name" required />
                  <Form.Item
                    name="firstName"
                    rules={[
                      {
                        required: true,
                        message: "Required",
                      },
                    ]}
                  >
                    <CustomInput placeholder="First name" />
                  </Form.Item>
                </div>
                <div className="flex flex-col gap-2 w-[48%]">
                  <Label text="En. Second Name" required />
                  <Form.Item
                    name="secondName"
                    rules={[
                      {
                        required: true,
                        message: "Required",
                      },
                    ]}
                  >
                    <CustomInput placeholder="Second name" />
                  </Form.Item>
                </div>
              </div>

              <div className="field flex items-start justify-between">
                <div className="flex flex-col gap-2 w-[48%]">
                  <Label text="En. Third Name" required />
                  <Form.Item
                    name="thirdName"
                    rules={[
                      {
                        required: true,
                        message: "Required",
                      },
                    ]}
                  >
                    <CustomInput placeholder="Third name" />
                  </Form.Item>
                </div>
                <div className="flex flex-col gap-2 w-[48%]">
                  <Label text="En. Family Name" required />
                  <Form.Item
                    name="familyName"
                    rules={[
                      {
                        required: true,
                        message: "Required",
                      },
                    ]}
                  >
                    <CustomInput placeholder="Family name" />
                  </Form.Item>
                </div>
              </div>

              <div className="field flex items-start justify-between">
                <div className="flex flex-col gap-2 w-[48%]">
                  <Label text="Ar. First Name" required />
                  <Form.Item
                    name="firstNameAr"
                    rules={[
                      {
                        required: true,
                        message: "Required",
                      },
                    ]}
                  >
                    <CustomInput placeholder="First name" dir="rtl" />
                  </Form.Item>
                </div>
                <div className="flex flex-col gap-2 w-[48%]">
                  <Label text="Ar. Second Name" required />
                  <Form.Item
                    name="secondNameAr"
                    rules={[
                      {
                        required: true,
                        message: "Required",
                      },
                    ]}
                  >
                    <CustomInput placeholder="Second name" dir="rtl" />
                  </Form.Item>
                </div>
              </div>

              <div className="field flex items-start justify-between">
                <div className="flex flex-col gap-2 w-[48%]">
                  <Label text="Ar. Third Name" required />
                  <Form.Item
                    name="thirdNameAr"
                    rules={[
                      {
                        required: true,
                        message: "Required",
                      },
                    ]}
                  >
                    <CustomInput placeholder="Third name" dir="rtl" />
                  </Form.Item>
                </div>
                <div className="flex flex-col gap-2 w-[48%]">
                  <Label text="Ar. Family Name" required />
                  <Form.Item
                    name="familyNameAr"
                    rules={[
                      {
                        required: true,
                        message: "Required",
                      },
                    ]}
                  >
                    <CustomInput placeholder="Family name" dir="rtl" />
                  </Form.Item>
                </div>
              </div>

              <div className="field flex flex-col">
                <Label text="Birth place - Country" required />
                <Form.Item
                  name="birthPlaceId"
                  rules={[
                    {
                      required: true,
                      message: "Please select the birth place - Country!",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select birth place - Country"
                    allowClear
                    showSearch
                    filterOption={(input, option: any) => {
                      return (option?.key.toLowerCase() ?? "").includes(
                        input.toLowerCase()
                      );
                    }}
                  >
                    {countriesData.map((country: any) => (
                      <Option value={country.id} key={country.nameEn}>
                        <div className="flex align-center" style={{ gap: "5px" }}>
                          <ReactCountryFlag
                            style={{
                              fontSize: "24px",
                              marginInlineEnd: "8px",
                            }}
                            countryCode={country?.countryCode}
                            svg
                          />
                          <span>{country.nameEn}</span>
                        </div>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              <div className="field flex justify-between">
                <div className="field flex flex-col">
                  <Label text="Date of Birth (Hijri)" required />
                  <Form.Item
                    name="birthDateHijri"
                    rules={[
                      {
                        required: true,
                        message: "Please input date of birth",
                      },
                    ]}
                    getValueFromEvent={(date: any) => date}
                  >
                    <HijriDatePicker
                      value={form.getFieldValue("birthDate")}
                      calendar={arabic}
                      locale={arabic_en}
                      format="YYYY-MM-DD"
                      placeholder="Select date of birth"
                      maxDate={new DateObject({ calendar: arabic, locale: arabic_en })}
                      style={{ width: "100%", height: "40px", fontSize: "14px" }}
                      containerStyle={{ width: "100%" }}
                      onChange={(date: any) => {
                        if (date) {
                          const gDate = new DateObject(date)
                            .convert(gregorian, gregorian_en)
                            .format("YYYY-MM-DD");
                          form.setFieldsValue({ birthDate: dayjs(gDate) });
                        } else {
                          form.setFieldsValue({ birthDate: null });
                        }
                      }}
                    />
                  </Form.Item>
                </div>

                <div className="field flex flex-col">
                  <Label text="Date of Birth (Gregorian)" />
                  <Form.Item name="birthDate">
                    <DatePicker
                      format={"YYYY-MM-DD"}
                      placeholder="Select date of birth"
                      disabledDate={(current) => current && current.isAfter(new Date())}
                      onChange={(date: any) => {
                        if (date) {
                          const hijriDate = new DateObject({
                            date: new Date(date.format("YYYY-MM-DD")),
                            calendar: arabic,
                            locale: arabic_en,
                          });
                          form.setFieldsValue({ birthDateHijri: hijriDate });
                        } else {
                          form.setFieldsValue({ birthDateHijri: null });
                        }
                      }}
                    />
                  </Form.Item>
                </div>
              </div>

              <div className="field flex flex-col">
                <Label text="Nationality" required />
                <Form.Item
                  name="nationalityId"
                  rules={[
                    {
                      required: true,
                      message: "Please select the nationality!",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select Nationality"
                    allowClear
                    showSearch
                    disabled
                    filterOption={(input, option: any) => {
                      return (option?.key.toLowerCase() ?? "").includes(
                        input.toLowerCase()
                      );
                    }}
                  >
                    {countriesData.map((country: any) => (
                      <Option value={country.id} key={country.nameEn}>
                        <div className="flex align-center" style={{ gap: "5px" }}>
                          <ReactCountryFlag
                            style={{
                              fontSize: "24px",
                              marginInlineEnd: "8px",
                            }}
                            countryCode={country?.countryCode}
                            svg
                          />
                          <span>{country.nameEn}</span>
                        </div>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              <div className="field flex flex-col">
                <Label text="Resident country" required />
                <Form.Item
                  name="countryId"
                  rules={[
                    {
                      required: true,
                      message: "Please select the Country!",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select Country"
                    allowClear
                    showSearch
                    disabled
                    filterOption={(input, option: any) => {
                      return (option?.key.toLowerCase() ?? "").includes(
                        input.toLowerCase()
                      );
                    }}
                  >
                    {countriesData.map((country: any) => (
                      <Option value={country.id} key={country.nameEn}>
                        <div className="flex align-center" style={{ gap: "5px" }}>
                          <ReactCountryFlag
                            style={{
                              fontSize: "24px",
                              marginInlineEnd: "8px",
                            }}
                            countryCode={country?.countryCode}
                            svg
                          />
                          <span>{country.nameEn}</span>
                        </div>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              <div className="field flex flex-col">
                <Label text="Gender" required />
                <Form.Item
                  name="genderId"
                  rules={[
                    {
                      required: true,
                      message: "Please select gender",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select gender"
                    options={getStandingDataOptions(standingData, "gender", [
                      "sa",
                      "gc",
                    ])}
                  />
                </Form.Item>
              </div>

              <div className="field flex flex-col">
                <Label text="Marital status" required />
                <Form.Item
                  name="maritalStatusId"
                  rules={[
                    {
                      required: true,
                      message: "Please select marital status",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select marital status"
                    options={getStandingDataOptions(
                      standingData,
                      "maritalStatus",
                      ["sa"]
                    )}
                  />
                </Form.Item>
              </div>

              <div className="field flex flex-col">
                <Label text={" Number of dependants"} />
                <Form.Item
                  name="numberDependents"
                >
                  <CustomInput
                    placeholder="Enter number of dependants"
                    type="number"
                  />
                </Form.Item>
              </div>


              <div className="field flex flex-col">
                <Label text="ID type" required />
                <Form.Item
                  name="idTypeId"
                  rules={[
                    {
                      required: true,
                      message: "Please select ID type",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select ID type"
                    options={getStandingDataOptions(
                      standingData,
                      "iDType",
                      ["sa"],
                      true
                    )}
                  />
                </Form.Item>
              </div>
              {form.getFieldValue("idTypeId") == "-1" ? (
                <div className="field flex flex-col">
                  <Label
                    text="Other ID type"
                    required
                  />
                  <Form.Item
                    name="otherIdType"
                    rules={[
                      {
                        required: true,
                        message: "Please input other ID number!",
                      },
                    ]}
                  >
                    <CustomInput placeholder="Enter other ID type" />
                  </Form.Item>
                </div>
              ) : null}


              {form.getFieldValue("idTypeId") !== -1 && (
                <div className="field flex flex-col">
                  <Label text={"ID Number"} required />
                  <Form.Item
                    name="civilIdNumber"
                    rules={[
                      {
                        required: true,
                        message: "Please input ID number!",
                      },
                      {
                        pattern: form.getFieldValue("idTypeId") == 192 ? /^1\d{9}$/ : /^2\d{9}$/,
                        message: form.getFieldValue("idTypeId") == 192 ? "ID number must be 10 digits and start with 1" : "ID number must be 10 digits and start with 2",
                      },
                    ]}
                  >
                    <CustomInput
                      placeholder="Enter ID Number"
                      maxLength={10}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        form.setFieldValue("civilIdNumber", value);
                      }}
                    />
                  </Form.Item>
                </div>
              )}

              <div className="field flex justify-between">
                {form.getFieldValue("idTypeId") !== -1 && (
                  <div className="field flex flex-col">
                    <Label text="ID Expiry Date (Hijri)" required />
                    <Form.Item
                      name="expiryDateHijri"
                      rules={[
                        {
                          required: true,
                          message: "Please input expiry date!",
                        },
                      ]}
                      getValueFromEvent={(date: any) => date}
                    >
                      <HijriDatePicker
                        value={form.getFieldValue("civilIdExpiry")}
                        calendar={arabic}
                        locale={arabic_en}
                        format="YYYY-MM-DD"
                        placeholder="Select ID expiry date"
                        minDate={new DateObject({ calendar: arabic, locale: arabic_en })}
                        style={{ width: "100%", height: "40px", fontSize: "14px" }}
                        containerStyle={{ width: "100%" }}
                        onChange={(date: any) => {
                          if (date) {
                            const gDate = new DateObject(date)
                              .convert(gregorian, gregorian_en)
                              .format("YYYY-MM-DD");
                            form.setFieldsValue({ civilIdExpiry: dayjs(gDate) });
                          } else {
                            form.setFieldsValue({ civilIdExpiry: null });
                          }
                        }}
                      />
                    </Form.Item>
                  </div>
                )}

                {form.getFieldValue("idTypeId") !== -1 && (
                  <div className="field flex flex-col">
                    <Label text="ID Expiry Date (Gregorian)" />
                    <Form.Item name="civilIdExpiry">
                      <DatePicker
                        format={"YYYY-MM-DD"}
                        placeholder="Select ID expiry date"
                        disabledDate={(current) => current && current.isBefore(new Date(), "day")}
                        onChange={(date: any) => {
                          if (date) {
                            const hijriDate = new DateObject({
                              date: new Date(date.format("YYYY-MM-DD")),
                              calendar: arabic,
                              locale: arabic_en,
                            });
                            form.setFieldsValue({ expiryDateHijri: hijriDate });
                          } else {
                            form.setFieldsValue({ expiryDateHijri: null });
                          }
                        }}
                      />
                    </Form.Item>
                  </div>
                )}
              </div>

              {form.getFieldValue("idTypeId") == -1 && (
                <div className="field flex flex-col">
                  <Label text="Passport number" required />
                  <Form.Item
                    name="passportNumber"
                    rules={[
                      {
                        required: true,
                        message: "Please input passport number!",
                      },
                    ]}
                  >
                    <CustomInput placeholder="Enter passport number" />
                  </Form.Item>
                </div>
              )}

              {form.getFieldValue("idTypeId") == -1 && (
                <div className="field flex flex-col">
                  <Label text="Passport expiry date" required />
                  <Form.Item
                    name="passportExpiry"
                    rules={[
                      {
                        required: true,
                        message: "Please input expiry date!",
                      },
                    ]}
                  >
                    <DatePicker
                      format={"YYYY-MM-DD"}
                      placeholder="Select expiry date"
                      onChange={(e: any) => e?.format("YYYY-MM-DD")}
                      disabledDate={(current) => current && current.isBefore(new Date(), "day")}
                    />
                  </Form.Item>
                </div>
              )}

              {form.getFieldValue("idTypeId") == -1 && (
                <div className="field flex flex-col">
                  <Label text="Place of issue" required />
                  <Form.Item
                    name="birthPlaceId"
                    rules={[
                      {
                        required: true,
                        message: "Please select the place of issue!",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select place of issue"
                      allowClear
                      showSearch
                      filterOption={(input, option: any) => {
                        return (option?.key.toLowerCase() ?? "").includes(
                          input.toLowerCase()
                        );
                      }}
                    >
                      {countriesData.map((country: any) => (
                        <Option value={country.id} key={country.nameEn}>
                          <div
                            className="flex align-center"
                            style={{ gap: "5px" }}
                          >
                            <ReactCountryFlag
                              style={{
                                fontSize: "24px",
                                marginInlineEnd: "8px",
                              }}
                              countryCode={country?.countryCode}
                              svg
                            />
                            <span>{country.nameEn}</span>
                          </div>
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              )}


              <div className="field flex flex-col">
                <Label
                  text="Is the client the beneficial owner of the account?"
                  required
                />
                <Form.Item
                  name="isBeneficialOwner"
                  rules={[
                    {
                      required: true,
                      message: "Please select if the client is the beneficial owner of the account!",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select if the client is the beneficial owner of the account"
                    options={[{ label: "Yes", value: true }, { label: "No", value: false }]}
                  />
                </Form.Item>
              </div>

              {form.getFieldValue("isBeneficialOwner") == false && (
                <div className="field flex flex-col">
                  <Label text="The identity of the beneficial owner of the account" required />
                  <Form.Item
                    name="benefeicialOwnerId"
                    rules={[
                      {
                        required: true,
                        message: "Please input the identity of the beneficial owner of the account!",
                      },
                      {
                        pattern: /^1\d{9}$/,
                        message: "ID number must be 10 digits and start with 1",
                      },
                    ]}
                  >
                    <CustomInput
                      maxLength={10}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        form.setFieldValue("benefeicialOwnerId", value);
                      }}
                      placeholder="Enter the identity of the beneficial owner of the account" />
                  </Form.Item>
                </div>
              )}
            </div>

            {accountDetails?.clientSubType?.description === "Minor" && (
              <>
                <div className="flex gap-3 items-center">
                  <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
                  <h3 className=" text-lg font-semibold">Guardian details</h3>
                </div>
                <div className="fields flex justify-space-between wrap">


                  <div className="field flex column">
                    <Label text="Full Name in English" required />
                    <Form.Item
                      name="guardianNameEn"

                    >
                      <CustomInput placeholder="Enter full name in English" />
                    </Form.Item>
                  </div>

                  <div className="field flex flex-col">
                    <Label text="Relationship with applicant" />
                    <Form.Item
                      name="guardianRelationshipWithApplicant"

                    >
                      <CustomInput placeholder="Enter relationship with applicant" />
                    </Form.Item>
                  </div>

                  <div className="field flex flex-col">
                    <Label text="ID Number" />
                    <Form.Item
                      name="guardianCivilId"
                      rules={[
                        {
                          required: true,
                          message: "Please input ID number!",
                        },
                        {
                          pattern: /^[12]\d{9}$/,
                          message: "ID number must be 10 digits and start with 1 or 2",
                        },
                      ]}
                    >
                      <CustomInput
                        placeholder="Enter ID Number"
                        maxLength={10}
                        onChange={(e: any) => {
                          const value = e.target.value.replace(/\D/g, "");
                          form.setFieldsValue({ guardianCivilId: value });
                        }}
                      />
                    </Form.Item>
                  </div>

                  <div className="field flex flex-col">
                    <Label text="Date of Expiry" />
                    <Form.Item
                      name="guardianCivilExpiry"

                    >
                      <DatePicker
                        format={"YYYY-MM-DD"}
                        placeholder="Select date of expiry"
                        onChange={(e: any) => e?.format("YYYY-MM-DD")}
                        disabledDate={(current) => current && current.isBefore(new Date(), "day")}
                      />
                    </Form.Item>
                  </div>

                  <div className="field flex flex-col">
                    <Label text="Place of issue" />
                    <Form.Item
                      name="guardianCivilIssuePlace"

                    >
                      <Select
                        placeholder="Select place of issue"
                        allowClear
                        showSearch
                        filterOption={(input, option: any) => {
                          return (option?.key.toLowerCase() ?? "").includes(
                            input.toLowerCase()
                          );
                        }}
                      >
                        {countriesData.map((country: any) => (
                          <Option value={country.countryCode} key={country.nameEn}>
                            <div
                              className="flex align-center"
                              style={{ gap: "5px" }}
                            >
                              <ReactCountryFlag
                                style={{
                                  fontSize: "24px",
                                  marginInlineEnd: "8px",
                                }}
                                countryCode={country?.countryCode}
                                svg
                              />
                              <span>{country.nameEn}</span>
                            </div>
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </div>

                  <div className="field flex flex-col">
                    <Label text="Deed Number" />
                    <Form.Item
                      name="guardianDeedNumber"
                    >
                      <CustomInput placeholder="Enter Deed Number" type="number" />
                    </Form.Item>
                  </div>


                  <div className="field flex flex-col">
                    <Label text="Deed Date" />
                    <Form.Item
                      name="guardianDeedDate"

                    >
                      <DatePicker
                        format={"YYYY-MM-DD"}
                        placeholder="Select deed date"
                        onChange={(e: any) => e?.format("YYYY-MM-DD")}
                        disabledDate={(current) => current && current.isAfter(new Date())}
                      />
                    </Form.Item>
                  </div>

                </div>
              </>
            )}
            {/* Contact Persons Section */}
            <div className="contact-persons-section mt-6">
              <div className="flex gap-3 items-center mb-4">
                <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
                <h3 className=" text-lg font-semibold">Contact Persons</h3>
              </div>
              <div className="add-field-button flex justify-start w-full">
                <Button
                  onClick={() => setOpenContactPersonsModal(true)}
                  className=" border-0 text-blue-500 bg-transparent mb-2 shadow-none p-0 text-base"
                // icon={<PlusSquareOutlined />}
                >
                  Manage Contact Persons
                </Button>
              </div>
            </div>

            <div className="flex w-full flex-wrap items-center justify-end mt-7  pt-5">
              <div className="flex justify-between items-center w-[70%] md:w-[48%]">
                <button
                  onClick={() => navigate(`/home/customers-list`)}
                  className="rounded-lg bg-white w-[48%] py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-blue-500"
                >
                  Cancel
                </button>
                <button
                  // disabled={!submitForm}
                  className="disabled:opacity-50 disabled:cursor-not-allowed rounded-lg bg-blue-500 w-[48%] py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-white"
                >
                  Save and continue
                </button>
              </div>
            </div>
          </Form>
        )}

        <CustomModal
          isVisible={openUpdateFatca}
          centered
          width={450}
          onClose={() => {
            setOpenUpdateFatca(false);
          }}
          className="delete-modal"
        >
          <div className="modal-container flex flex-col items-center justify-center">
            <img
              src="/images/fatca-update.svg"
              alt="kamco update fatca"
              className="mb-3"
            />
            <p className=" text-center">
              You need to update FATCA information because you have an american
              nationality!
            </p>
            <div className="buttons-container flex items-center justify-between">
              <button
                className="cancel flex items-center justify-center"
                onClick={() =>
                  navigate(
                    `/home/business-account/${accountId}/${activeKyc}/details?tab=1${isApproveCase ? "&approve=1" : ""
                    }`
                  )
                }
              >
                Cancel
              </button>

              <button
                className="flex items-center justify-center"
                onClick={() => navigate(`/crs/${accountId}/${activeKyc}/edit`)}
              >
                Update
              </button>
            </div>
          </div>
        </CustomModal>

        {/* Contact Persons Modal */}
        <CustomModal
          isVisible={openContactPersonsModal}
          centered
          width={900}
          onClose={() => {
            setOpenContactPersonsModal(false);
          }}
        >
          <div className="contact-persons-modal-content p-5 edit-contact-persons-container">
            <h3 className="font-semibold text-xl mb-5">Manage Contact Persons</h3>
            {contactPersonsArray?.map((person: any, index: number) => (
              <CollapseComponent
                key={index}
                header={
                  <div className="flex gap-3 items-center">
                    <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
                    <h3 className=" text-lg font-semibold">
                      Person #{index + 1}
                    </h3>
                  </div>
                }
              >
                <ContactPersonForm
                  person={person}
                  index={index}
                  standingData={standingData}
                  countriesList={countriesData}
                  setSelectedContactPerson={setSelectedContactPerson}
                  setOpenDeleteModal={setOpenDeleteModal}
                  onSubmit={onContactPersonSubmit}
                />
              </CollapseComponent>
            ))}
            <div className="add-field-button flex justify-start w-full mt-4">
              <Button
                onClick={() => {
                  setContactPersonsArray((prev: any) => [
                    ...prev,
                    {
                      id: null,
                      prefix: null,
                      fullName: null,
                      relationshipId: null,
                      email: null,
                      mobileNumber: null,
                    },
                  ]);
                }}
                className=" border-0 text-blue-500 bg-transparent mb-2 shadow-none p-0 text-base"
                icon={<PlusSquareOutlined />}
              >
                Add a new contact person
              </Button>
            </div>
          </div>
        </CustomModal>

        {/* Delete Contact Person Modal */}
        <CustomModal
          isVisible={openDeleteModal}
          centered
          width={450}
          onClose={() => {
            setOpenDeleteModal(false);
          }}
          className="delete-modal"
        >
          <DeleteModal
            onCancel={() => setOpenDeleteModal(false)}
            onConfirm={deleteContactPerson}
            text="Delete contact person!"
            subText={`Are you sure you want to delete this contact person?`}
          />
        </CustomModal>
      </div>
    );
  };

export default EditPersonalDetails;
